// Core simulation types. Every other module in src/sim and the UI depends on these.

import type { RulesOverrides, RulesPreset } from './rules';

export type Side = 0 | 1; // 0 blue (player), 1 red (enemy)
export type UnitType = 'armor' | 'mech' | 'inf' | 'recon' | 'spArty' | 'mortar' | 'uas' | 'hq' | 'log';
export type Mobility = 'tracked' | 'wheeled' | 'foot' | 'air';
export type Posture = 'move' | 'attack' | 'defend' | 'hold' | 'withdraw';
export type Family = 'maneuver' | 'fires' | 'isr' | 'sustain' | 'command';
export type CardId =
  | 'move' | 'attack' | 'defend' | 'screen' | 'withdraw'
  | 'fireMission' | 'smoke' | 'suppress'
  | 'launchUas' | 'recon' | 'observe'
  | 'resupply' | 'casevac'
  | 'cancel' | 'priorityFires' | 'mainEffort' | 'frago';
export type TargetKind = 'point' | 'graphic' | 'unit' | 'contact' | 'none' | 'text';
export type OrderStatus = 'queued' | 'transmitting' | 'received' | 'executing' | 'done' | 'cancelled' | 'rejected';
export type GraphicKind = 'axis' | 'obj' | 'pl' | 'nai' | 'aa';

export interface UnitDef {
  type: UnitType; label: string; mobility: Mobility; speedKph: number;
  sensorRangeM: number; sensorQuality: number; weaponRangeM: number; firepower: number; armor: number;
  fuelPerKm: number; ammoPerMin: number; heightM: number; sidcBlue: string; sidcRed: string;
}

export interface Unit {
  id: number; side: Side; type: UnitType; callsign: string;
  x: number; y: number;                 // integer centimeters from the AO south-west corner
  strength: number; suppression: number; morale: number; fuel: number; ammo: number; // 0..100
  posture: Posture; alive: boolean; inContact: boolean; contactSince: number;
  destCell: number;                     // -1 none
  orderId: number;                      // -1 none
  replanUntil: number;                  // tick until which the unit is re-planning after a re-task
  brokenUntil: number;                  // tick; broken units withdraw until this
  uasAlive: boolean;                    // for type 'uas': airframe is up
  lastReportTick: number;
  movedCm: number;                      // distance moved this tick (for fuel)
  pinned: boolean;
  firedThisMin: boolean;                // was fired upon in the current minute (suppression decay gate)
  crossedPls: string[];                 // graphic ids of phase lines already crossed
}

export interface Graphic {
  id: string; kind: GraphicKind; name: string;   // "OBJ COBALT", "PL RED", "NAI 2", "AXIS NORTH"
  pts: { x: number; y: number }[];               // cm
  radiusM?: number;
}

export interface Target { kind: TargetKind; cell?: number; graphicId?: string; unitId?: number; text?: string }

export interface CardDef {
  id: CardId; family: Family; label: string; blurb: string; issueSec: number; execSec: number;
  targetKind: TargetKind; allowedTypes: UnitType[];
}

export interface Order {
  id: number; side: Side; unitId: number; card: CardId; target: Target;
  issuedAt: number; netStart: number; netEnd: number; txDone: number; execStart: number;
  status: OrderStatus; reason?: string; playId?: string;
}

export interface Contact {
  enemyId: number; x: number; y: number; errorM: number;
  typeConf: 'unknown' | 'vehicle' | UnitType;
  lastSeen: number; reportedAt: number; observerId: number;
}

export interface Picture {
  side: Side;
  contacts: Map<number, Contact>;
  reported: Map<number, { x: number; y: number; tick: number }>;
}

export type EventKind =
  | 'contact.new' | 'contact.lost'
  | 'order.issued' | 'order.tx' | 'order.received' | 'order.exec' | 'order.done' | 'order.cancelled' | 'order.rejected'
  | 'unit.pinned' | 'unit.unpinned' | 'unit.broken' | 'unit.destroyed' | 'unit.fuelLow' | 'unit.ammoLow'
  | 'force.fuelLow' | 'fire.mission' | 'uas.lost' | 'pl.crossed' | 'nai.contact' | 'obj.seized'
  | 'net.saturated' | 'red.decision' | 'phase' | 'culminated' | 'sitrep';

export interface Event {
  id: number; t: number; kind: EventKind; side: Side; unitId?: number; cell?: number;
  payload: Record<string, unknown>; significant: boolean;
}

export interface Dials {
  c2: 'off' | 'realistic' | 'degraded';
  ew: 'none' | 'moderate' | 'heavy';
  tod: 'dawn' | 'day' | 'night';
  red: 'screen' | 'depth' | 'forward';
  pressure: boolean;
  planMin: 3 | 5 | 10;
  /** The ruleset the sim runs under (pitch 10): a named preset plus any Instructor-panel overrides. */
  rules: { preset: RulesPreset; overrides?: RulesOverrides };
}

export const DEFAULT_DIALS: Dials = {
  c2: 'realistic', ew: 'moderate', tod: 'dawn', red: 'depth', pressure: false, planMin: 5,
  rules: { preset: 'realistic' },
};

export interface Placement { type: UnitType; callsign: string; x: number; y: number }

export interface Scenario {
  ao: string; seed: number; blue: Placement[]; red: Placement[];
  graphics: Graphic[]; objectiveId: string; assemblyId: string;
  names: { tf: string; obj: string; enemy: string };
  timeLimitMin: number;
}

export interface NetState { busyUntil: number; busyTicks: number[] } // busyTicks: ticks (start,end) pairs flattened for occupancy window

export interface Snapshot {
  tick: number;
  units: Unit[];
  picture: { contacts: Contact[]; reported: [number, { x: number; y: number; tick: number }][] };
  orders: Order[];
  net: { occupancy: number; queueSec: number };
  force: { strength: number; fuel: number; ammo: number };
  phase: string;
  ended: null | 'seized' | 'culminated' | 'time';
}

export interface AoMeta {
  name: string; title: string; lat: number; lon: number; sizeM: number; cellM: number; w: number; h: number;
  originLat: number; originLon: number; mPerDegLat: number; mPerDegLon: number; elevMin?: number; elevMax?: number;
}

export const TICKS_PER_MIN = 10;
export const TICK_SEC = 6;
export const secToTicks = (sec: number): number => Math.ceil(sec / TICK_SEC);
export const minToTicks = (min: number): number => Math.round(min * TICKS_PER_MIN);
